/** Opening-hours model shared by web, functions and the import CLI. Times are 24h "HH:MM". */
export const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type DayKey = (typeof DAYS)[number]

export const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
}
export const DAY_SHORT: Record<DayKey, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
}

export type TimeRange = { open: string; close: string }
export type DayHours = { closed: boolean; ranges: TimeRange[] }
export type Schedule = {
  id: string
  label: string
  days: Record<DayKey, DayHours>
}

export const SCHEDULE_PRESETS = [
  { id: 'regular', label: 'Regular hours' },
  { id: 'lunch', label: 'Lunch' },
  { id: 'brunch', label: 'Brunch' },
  { id: 'dinner', label: 'Dinner' },
  { id: 'kitchen', label: 'Kitchen' },
  { id: 'bar', label: 'Bar' },
  { id: 'late-night', label: 'Late night' },
] as const

export function emptyDay(): DayHours {
  return { closed: true, ranges: [] }
}
export function openDay(open = '11:00', close = '22:00'): DayHours {
  return { closed: false, ranges: [{ open, close }] }
}
export function emptySchedule(id: string, label: string): Schedule {
  return {
    id,
    label,
    days: { mon: emptyDay(), tue: emptyDay(), wed: emptyDay(), thu: emptyDay(), fri: emptyDay(), sat: emptyDay(), sun: emptyDay() },
  }
}

/** 15-minute options for time selects, "00:00" … "23:45". */
export const TIME_OPTIONS: string[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4)
  const m = (i % 4) * 15
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
})

export function to12h(hhmm: string): string {
  const [hs, ms] = hhmm.split(':')
  const h = Number(hs)
  const m = ms ?? '00'
  if (Number.isNaN(h)) return hhmm
  const suffix = h < 12 ? 'am' : 'pm'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return m === '00' ? `${h12}${suffix}` : `${h12}:${m}${suffix}`
}

export function dayText(d: DayHours): string {
  if (d.closed || d.ranges.length === 0) return 'Closed'
  return d.ranges.map((r) => `${to12h(r.open)}–${to12h(r.close)}`).join(', ')
}

/** Collapse consecutive days with identical hours: "Mon–Fri 11am–3pm, 4:30pm–11pm". */
export function scheduleText(s: Schedule): string {
  const out: string[] = []
  let i = 0
  while (i < DAYS.length) {
    const start = i
    const text = dayText(s.days[DAYS[i]!])
    while (i + 1 < DAYS.length && dayText(s.days[DAYS[i + 1]!]) === text) i++
    const label = start === i ? DAY_SHORT[DAYS[start]!] : `${DAY_SHORT[DAYS[start]!]}–${DAY_SHORT[DAYS[i]!]}`
    out.push(`${label} ${text}`)
    i++
  }
  return out.join(' · ')
}

/**
 * A special or promotion: a named offer worth posting about (Taco Tuesday, ladies' night, a wine dinner).
 * Opening hours and service periods are Schedules; anything with a deal or a theme is a Promotion.
 */
export type Promotion = {
  id: string
  name: string
  details: string
  repeat: 'weekly' | 'once'
  /** Weekly: the days it runs. */
  days: DayKey[]
  /** Once: "YYYY-MM-DD". */
  date: string | null
  allDay: boolean
  /** "HH:MM" when not all day. */
  start: string | null
  end: string | null
}

export function newPromotion(id: string): Promotion {
  return { id, name: '', details: '', repeat: 'weekly', days: [], date: null, allDay: true, start: null, end: null }
}

/** "Every day", "Tue–Thu", "Mon, Wed, Fri". */
export function daysText(days: readonly DayKey[]): string {
  const on = DAYS.filter((d) => days.includes(d))
  if (on.length === 7) return 'Every day'
  if (on.length === 0) return ''
  const parts: string[] = []
  let i = 0
  while (i < on.length) {
    const start = i
    while (i + 1 < on.length && DAYS.indexOf(on[i + 1]!) === DAYS.indexOf(on[i]!) + 1) i++
    parts.push(i - start >= 2 ? `${DAY_SHORT[on[start]!]}–${DAY_SHORT[on[i]!]}` : on.slice(start, i + 1).map((d) => DAY_SHORT[d]).join(', '))
    i++
  }
  return parts.join(', ')
}

/** One line for a promotion's timing: "Tue–Thu · 7pm–11pm", "Fri, Oct 24 · All day". */
export function promotionWhen(p: Promotion): string {
  const when =
    p.repeat === 'once'
      ? p.date
        ? new Date(`${p.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        : 'Date to be set'
      : daysText(p.days) || 'Days to be set'
  const time = p.allDay || !p.start || !p.end ? 'All day' : `${to12h(p.start)}–${to12h(p.end)}`
  return `${when} · ${time}`
}
